"use client";
import React, { useEffect, useState } from "react";
import { EmailIcon, PasswordIcon } from "@/assets/icons";
import Link from "next/link";
import { useRouter } from "next/navigation";

import InputGroup from "../FormElements/InputGroup";
import { Checkbox } from "../FormElements/checkbox";
import toast from "react-hot-toast";
import localStorageService from "@/utils/localStorageService";
import { USER_LOGIN_DATA } from "@/utils/constants";
import { isMobileDevice } from "@/utils";
import { handleAuthResponse } from "@/lib/auth-handler";

export default function SigninWithPassword() {
  const [data, setData] = useState({
    email: "",
    password: "",
    remember: false,
    isMobile: false,
  });

  const [loading, setLoading] = useState(false);

  const router = useRouter();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData({
      ...data,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const { email, password, isMobile } = data;

    if (!email) {
      toast.error(`Please enter your email`);
      return;
    }

    if (!password) {
      toast.error(`Please enter your password`);
      return;
    }

    // Show loader on btn
    setLoading(true);

    try {
      // Call the server-side login API route
      const response = await fetch("/api/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password, isMobile }),
        cache: "no-cache",
      });

      const result = await response.json();

      if (response?.ok) {
        // localStorageService.setItem(USER_LOGIN_DATA, result);
        await handleAuthResponse(result);
        toast.success("Login successful!");
        router.push("/dashboard/"); // Redirect to dashboard after login
      } else {
        toast.error(result?.message || "Login failed");
      }
    } catch (error) {
      toast.error(`Error during login: ${error}`);
      console.error("Error during login:", error);
    }
    // Hide loader on btn
    setLoading(false);
  };

  useEffect(() => {
    setData((prev) => ({
      ...prev,
      isMobile: isMobileDevice(),
    }));
  }, []);

  return (
    <form onSubmit={handleSubmit}>
      <InputGroup
        type="email"
        label="Email"
        className="mb-4 [&_input]:py-[15px]"
        placeholder="Enter your email"
        name="email"
        handleChange={handleChange}
        value={data.email}
        icon={<EmailIcon />}
        required
      />

      <InputGroup
        type="password"
        label="Password"
        className="mb-5 [&_input]:py-[15px]"
        placeholder="Enter your password"
        name="password"
        handleChange={handleChange}
        value={data.password}
        icon={<PasswordIcon />}
        required
      />

      <div className="mb-6 flex items-center justify-between gap-2 py-2 font-medium">
        <Checkbox
          label="Remember me"
          name="remember"
          withIcon="check"
          minimal
          radius="md"
          onChange={(e) =>
            setData({
              ...data,
              remember: e.target.checked,
            })
          }
          checked={data?.remember}
        />

        <Link
          href="#"
          className="hover:text-primary dark:text-white dark:hover:text-primary"
        >
          Forgot Password?
        </Link>
      </div>

      <div className="mb-4.5">
        <button
          type="submit"
          className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-primary p-4 font-medium text-white transition hover:bg-opacity-90"
        >
          Sign In
          {loading && (
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-t-transparent dark:border-primary dark:border-t-transparent" />
          )}
        </button>
      </div>
      <div className="mt-6 text-center">
        <p>
          Don't have an account?{" "}
          <Link href="/sign-up" className="text-primary">
            Sign Up
          </Link>
        </p>
      </div>
    </form>
  );
}
